import { DataGrid } from "./DataGrid";
import { SearchForm } from "./SearchForm";
import type {
  BulkPromote,
  LocalizedVehicleData,
  SearchViewProps,
  VehicleSearchFilters
} from "@dealer-center/inventory-pages-gvm-interface";
import { Button, useToaster } from "@dealer-center/core-components";
import {
  useBulkPromote,
  useEvictCache,
  useGetLocalizedVehicleData,
  useGetVehicleSearch,
  useVehicleSearch
} from "@dealer-center/inventory-pages-gvm-fetch";
import { useEffect, useState } from "react";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";
import { saveLiveQueryFilters } from "@dealer-center/inventory-core-utils";

const empty: LocalizedVehicleData = {
  locales: [],
  stages: []
};

export const SearchView = ({ renderLoadingData }: SearchViewProps) => {
  const localizedVehicleDataQuery = useGetLocalizedVehicleData();
  const {
    searchFilters,
    setSearchFilters,
    searchFiltersCopy,
    setSearchFiltersCopy,
    syncSearchFilters
  } = useVehicleSearch();
  const vehicleSearchQuery = useGetVehicleSearch(searchFiltersCopy);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [selectedTrims, setSelectedTrims] = useState<string[]>([]);
  const resetAllSelected = () => {
    setSelectedVehicles([]);
    setSelectedTrims([]);
  };
  const flushMutation = useEvictCache(resetAllSelected);
  const bulkPromoteMutation = useBulkPromote(resetAllSelected);
  const { toastError } = useToaster();
  const isQA = searchFilters.stage === "QA";
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const isButtonDisabled =
    (selectedVehicles.length == 0 && selectedTrims.length == 0) ||
    bulkPromoteMutation.isPending ||
    flushMutation.isPending ||
    !isGvmRwAccessUser;

  useEffect(() => {
    let message: string | null = null;

    if (localizedVehicleDataQuery.error) {
      message = "Error loading Catalog Data";
    } else if (vehicleSearchQuery.error) {
      message = "Error searching vehicles";
    } else if (bulkPromoteMutation.error) {
      message = "Error promoting vehicles/trims";
    }

    if (message) {
      toastError(message);
    }
  }, [
    localizedVehicleDataQuery.error,
    vehicleSearchQuery.error,
    bulkPromoteMutation.error
  ]);

  const onPagination = (selectedPage: number) => {
    const newOffset = selectedPage * searchFilters.max - searchFilters.max;

    const updater = (prev: VehicleSearchFilters) => {
      const filters = {
        ...prev,
        offset: newOffset,
        page: selectedPage
      };
      saveLiveQueryFilters(filters);
      return filters;
    };

    syncSearchFilters(updater);
  };

  const onPageResize = (selectedMax: number) => {
    const updater = (prev: VehicleSearchFilters) => {
      const filters = {
        ...prev,
        offset: 0,
        page: 1,
        max: Number(selectedMax)
      };
      saveLiveQueryFilters(filters);
      return filters;
    };

    syncSearchFilters(updater);
  };

  const handleVehicleFlush = () => {
    flushMutation.mutate({
      vehicleIds: selectedVehicles,
      trimIds: selectedTrims
    });
  };

  const handleBulkPromote = () => {
    const bulkPromote: BulkPromote = {
      locale: searchFilters.locale,
      vehicleIds: selectedVehicles,
      trimIds: selectedTrims
    };
    bulkPromoteMutation.mutate(bulkPromote);
  };

  const renderDataGrid = () => {
    return (
      <DataGrid
        locale={searchFiltersCopy.locale}
        max={searchFiltersCopy.max}
        page={searchFiltersCopy.page}
        selectedTrims={selectedTrims}
        selectedVehicles={selectedVehicles}
        setSelectedTrims={setSelectedTrims}
        setSelectedVehicles={setSelectedVehicles}
        stage={searchFiltersCopy.stage}
        vehicleSearchResponse={vehicleSearchQuery.data}
        onPageResize={onPageResize}
        onPagination={onPagination}
      />
    );
  };

  const renderQAActions = () => {
    const display =
      isQA &&
      !!vehicleSearchQuery.data &&
      vehicleSearchQuery.data.results.length > 0;

    return display ? (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "10px",
          marginBottom: "20px"
        }}
      >
        <Button
          analyticsConfiguration={{
            eventName: "Flush caches clicked"
          }}
          buttonStyle="secondary"
          data-testid="vehicle-search-flush-cache"
          disabled={isButtonDisabled}
          type="button"
          onClick={handleVehicleFlush}
        >
          {flushMutation.isPending ? "Flushing..." : "Flush Caches"}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "Promote selected clicked"
          }}
          buttonStyle="secondary"
          data-testid="vehicle-search-promote-selected"
          disabled={isButtonDisabled}
          type="button"
          onClick={handleBulkPromote}
        >
          {bulkPromoteMutation.isPending ? "Promoting..." : "Promote Selected"}
        </Button>
      </div>
    ) : null;
  };

  const render = () => {
    if (localizedVehicleDataQuery.isLoading) {
      return renderLoadingData();
    }

    const dataGrid =
      vehicleSearchQuery.isLoading || vehicleSearchQuery.isFetching
        ? renderLoadingData()
        : renderDataGrid();

    return (
      <>
        <SearchForm
          isSearching={
            vehicleSearchQuery.isLoading || vehicleSearchQuery.isFetching
          }
          localizedVehicleData={localizedVehicleDataQuery.data || empty}
          resetAllSelected={resetAllSelected}
          searchFilters={searchFilters}
          searchFiltersCopy={searchFiltersCopy}
          setSearchFilters={setSearchFilters}
          setSearchFiltersCopy={setSearchFiltersCopy}
        />

        {dataGrid}
        {renderQAActions()}
      </>
    );
  };

  return render() || null;
};
